import React, { useState, useEffect } from "react";
import { array, bool, string, func, object } from "prop-types";
import { useSelector } from "react-redux";
import { CloseIcon } from "../../../arrow/components/Svgs/close-icon";
import { BulletList } from "../../atoms/icon/icons/bullet-list";
import get from "lodash/get";
import "./local-nav-bar.m.css";
import { LeftArrowIcon } from "../../../../assets/icons/LeftArrowIcon";
import RsvpIcon from "../../../../assets/icons/rsvp";

export const LocalNavBar = ({ links = [], buttons = [], isParticipantsView = false, participantsTitle = "", title = "", onBack, onBookSeatClick, onKeepMeInformedClick, hasUserPurchasedStory = false, showStickyButtons, storyTemplate = "", onRsvpClick, isPreviousEvent = false, isRsvpModalOpen = false, onGiveFeedbackClick = null, member = null, feedBackLink }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const isMobileMenuOpen = useSelector(state => get(state, ["isMobileMenuOpen"], false));

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  const sectionMap = {
    'overview': ['[data-section="overview"]', '[class*="large-text-paragraph"]'],
    'speakers': ['[data-section="speakers"]', '[class*="speakers-section"]'],
    'agenda': ['[data-section="agenda"]', '[class*="agenda-section"]'],
    'sponsors': ['[data-section="sponsors"]', '[class*="sponsors-section"]'],
    'participants': ['[data-section="participants"]', '[class*="participants-section"]'],
    'book a seat': ['[data-section="book-seat"]', '[class*="book-seat-section"]'],
    'venue & contact': ['[data-section="venue-contact"]', '[class*="event-details-section"]'],
    'outcomes': ['#outcomes-heading', '[data-section="outcome"]', '[class*="event-outcomes"]'],
    'key takeaways': ['[data-section="key-takeaways"]', '[class*="key-takeaways-section"]'],
    'venue': ['[data-section="venue-contact"]', '[class*="event-details-section"]'],
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 3;

      // Create a map of available link texts for priority matching
      const availableLinkTexts = new Set(links.map(link => link.text.toLowerCase()));

      for (const [sectionKey, selectors] of Object.entries(sectionMap)) {
        let element = null;

        for (const selector of selectors) {
          element = document.querySelector(selector);
          if (element) break;
        }

        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;
          const elementBottom = elementTop + rect.height;

          if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
            // Prioritize matching the actual available link text
            if (availableLinkTexts.has(sectionKey)) {
              setActiveSection(sectionKey);
            } else if ((sectionKey === 'venue & contact' || sectionKey === 'venue') &&
                      (availableLinkTexts.has('venue') || availableLinkTexts.has('venue & contact'))) {
              // Handle venue/venue & contact mapping
              const matchingVenueKey = availableLinkTexts.has('venue') ? 'venue' : 'venue & contact';
              setActiveSection(matchingVenueKey);
            } else {
              setActiveSection(sectionKey);
            }
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [sectionMap, links]);

  const getButtonHandler = (button) => {
    switch (button.text) {
      case "Book a seat":
        return hasUserPurchasedStory ? undefined : onBookSeatClick;
      case "Keep me informed":
        return onKeepMeInformedClick ? onKeepMeInformedClick : button.onClick;
      case "RSVP":
        return onRsvpClick ? onRsvpClick : button.onClick;
      case "Give feedback":
        return onGiveFeedbackClick ? onGiveFeedbackClick : button.onClick;
      case "Sign In":
      case "Become a member":
        return button.onClick;
      default:
        return button.onClick;
    }
  };

  const isInteractiveButton = (button) => {
    if (button.text === "Book a seat" && hasUserPurchasedStory) {
      return false;
    }
    if (button.text === "Give feedback" && feedBackLink) {
      return true;
    }
    return button.text === "Book a seat" || button.text === "Keep me informed" || button.text === "RSVP" || button.text === "Sign In" || button.text === "Become a member" || button.onClick;
  };

  const getNavButtonStyle = (button) => {
    if (isInteractiveButton(button)) return { cursor: 'pointer' };
    if (button.text === "Give feedback") {
      return {
        cursor: 'not-allowed',
        backgroundColor: 'var(--background-bg-secondary, #F2F2F2)',
        color: 'var(--Text-text-disabled, #BFBFBF)',
        border: '1px solid var(--background-bg-secondary, #F2F2F2)',
      };
    }
    return { cursor: 'not-allowed', opacity: 0.5 };
  };

  const getButtonKeyDownHandler = (button) => {
    const handler = getButtonHandler(button);
    return handler ? (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handler();
      }
    } : undefined;
  };

  const handleLinkClick = (linkText) => {
    if (!linkText) return;

    const targetKey = linkText.toLowerCase();
    const selectors = sectionMap[targetKey];

    // Update active section immediately on click
    setActiveSection(targetKey);

    if (selectors) {
      let element = null;

      // Try each selector in the array until we find an element
      if (Array.isArray(selectors)) {
        for (const selector of selectors) {
          element = document.querySelector(selector);
          if (element) break;
        }
      } else {
        // Fallback for string selector
        element = document.querySelector(selectors);
      }

      if (element) {
        // Calculate offset for sticky navbar (adjust this value as needed)
        const navbarHeight = 80; // Adjust this value based on your navbar height
        const elementTop = element.getBoundingClientRect().top + window.pageYOffset - navbarHeight;

        window.scrollTo({
          top: elementTop,
          behavior: 'smooth'
        });
      } else {
        // If element not found, wait for DOM to load and try again
        setTimeout(() => {
          let retryElement = null;
          if (Array.isArray(selectors)) {
            for (const selector of selectors) {
              retryElement = document.querySelector(selector);
              if (retryElement) break;
            }
          } else {
            retryElement = document.querySelector(selectors);
          }

          if (retryElement) {
            // Calculate offset for sticky navbar
            const navbarHeight = 80;
            const elementTop = retryElement.getBoundingClientRect().top + window.pageYOffset - navbarHeight;

            window.scrollTo({
              top: elementTop,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    }

    // Close mobile menu if open
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  return (
    <>
      <div styleName="local-nav-bar">
        <div styleName="nav-content">
          {isParticipantsView ? (
            <div styleName="back-navigation" onClick={onBack} role="button" tabIndex={0} onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onBack && onBack();
              }
            }}>
              <LeftArrowIcon styleName="back-icon" stroke="white" />
              <div styleName="participants-title">
                {participantsTitle}
              </div>
            </div>
          ) : <div styleName="anchor-links">
            {links && links.map((link, index) => (
              <div
                key={index}
                styleName={`anchor-link ${activeSection === link.text.toLowerCase() ? "active" : ""}`}
                onClick={() => handleLinkClick(link.text)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleLinkClick(link.text);
                  }
                }}
              >
                {link.text}
              </div>
            ))}
          </div>}
          {buttons && (
            <div styleName={`button-unit ${showStickyButtons && storyTemplate !== "session" ? 'hide-on-tablet' : ''}`}>
              {storyTemplate === "session" && !isPreviousEvent && member && (
                <div styleName="are-you-coming-text">
                  Are you coming?
                </div>
              )}
              {buttons.map((button, index) => (
                <React.Fragment key={index}>
                  <div
                    styleName={`nav-button ${button.variant || "outline"} ${button.text === "Book a seat" && hasUserPurchasedStory ? "disabled" : ""} ${button.text === "RSVP" ? "nav-rsvp-button" : ""}`}
                    onClick={getButtonHandler(button)}
                    role={isInteractiveButton(button) ? "button" : undefined}
                    tabIndex={isInteractiveButton(button) ? 0 : undefined}
                    onKeyDown={getButtonKeyDownHandler(button)}
                    style={getNavButtonStyle(button)}
                    data-auth={storyTemplate === "session" && !member && (button.text === "Sign In" || button.text === "Become a member") ? "true" : undefined}
                  >
                    {button.text === "RSVP" && <div styleName="rsvp-icon"><RsvpIcon /></div>}
                    {button.text}
                  </div>
                  {storyTemplate === "session" && !member && button.text === "Sign In" && index < buttons.length - 1 && (
                    <div styleName="or-text">or</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Mobile title */}
        <div styleName="mobile-nav-title">
          {isParticipantsView ? (
            <div
              styleName="back-navigation"
              onClick={onBack}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onBack && onBack();
                }
              }}
            >
              <LeftArrowIcon styleName="back-icon" stroke="white" />
              <span>{participantsTitle}</span>
            </div>
          ) : (
            title
          )}
        </div>
      </div>

      {/* Mobile hamburger menu - fixed position */}
      {!isMobileMenuOpen && !isParticipantsView && !isRsvpModalOpen && (
        <div styleName="mobile-hamburger-wrapper">
          <button
            styleName="hamburger-button"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <CloseIcon /> : <BulletList size={24} />}
          </button>
        </div>
      )}

      {/* Mobile dropdown menu */}
      {
        isMenuOpen && !isParticipantsView && (
          <div styleName="mobile-dropdown" onClick={toggleMenu}>
            <div styleName="mobile-links" onClick={(e) => e.stopPropagation()}>
              {links.map(({ text }, index) => (
                <div
                  key={index}
                  styleName={`mobile-link ${activeSection === text.toLowerCase() ? "active" : ""}`}
                  onClick={() => handleLinkClick(text)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleLinkClick(text);
                    }
                  }}
                >
                  {text}
                </div>
              ))}
            </div>
          </div>
        )
      }

      {/* Sticky bottom buttons for mobile and tablet */}
      <div styleName={`sticky-buttons ${showStickyButtons && storyTemplate !== "session" ? 'tablet-enabled' : ''}`} data-story-template={storyTemplate}>
        {storyTemplate === "session" && !isPreviousEvent && member && (
          <div styleName="sticky-are-you-coming-text">
            Are you coming?
          </div>
        )}
        {buttons && buttons.map((button, index) => (
          <React.Fragment key={index}>
            <div
              styleName={`sticky-button ${button.variant || "outline"} ${button.text === "Book a seat" && hasUserPurchasedStory ? "disabled" : ""}`}
              onClick={getButtonHandler(button)}
              role={isInteractiveButton(button) ? "button" : undefined}
              tabIndex={isInteractiveButton(button) ? 0 : undefined}
              onKeyDown={getButtonKeyDownHandler(button)}
              style={getNavButtonStyle(button)}
              data-text={button.text}
              data-auth={storyTemplate === "session" && !member && (button.text === "Sign In" || button.text === "Become a member") ? "true" : undefined}
            >
              {button.text === "RSVP" && <div styleName="rsvp-icon"><RsvpIcon /></div>}
              {button.text}
            </div>
            {storyTemplate === "session" && !member && button.text === "Sign In" && index < buttons.length - 1 && (
              <div styleName="sticky-or-text">or</div>
            )}
          </React.Fragment>
        ))}
      </div>

    </>
  );
};

LocalNavBar.propTypes = {
  links: array,
  buttons: array,
  isParticipantsView: bool,
  participantsTitle: string,
  title: string,
  onBack: func,
  onBookSeatClick: func,
  onKeepMeInformedClick: func,
  onRsvpClick: func,
  hasUserPurchasedStory: bool,
  member: object,
};